import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CertificateEditorLayout } from './editor/CertificateEditorLayout';
import { CertificateTemplate } from './types';

interface TemplateEditorProps {
  templateId: string;
  onBack?: () => void;
}

export function TemplateEditor({ templateId, onBack }: TemplateEditorProps) {
  const queryClient = useQueryClient();
  const [htmlContent, setHtmlContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch template data
  const { data: template, isLoading, error } = useQuery({
    queryKey: ['certificate-template', templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      return data as CertificateTemplate;
    },
  });

  // Initialize HTML content when template loads
  useEffect(() => {
    if (template?.html_content) {
      setHtmlContent(template.html_content);
      setHasUnsavedChanges(false);
    }
  }, [template]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updatedHtml: string) => {
      const { data, error } = await supabase
        .from('certificate_templates')
        .update({
          html_content: updatedHtml,
          updated_at: new Date().toISOString()
        })
        .eq('id', templateId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate-template', templateId] });
      queryClient.invalidateQueries({ queryKey: ['certificate-templates'] });
      toast.success('Certificate template saved successfully');
      setHasUnsavedChanges(false);
    },
    onError: (error) => {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    },
  });

  const handleContentChange = (value: string) => {
    setHtmlContent(value);
    setHasUnsavedChanges(value !== template?.html_content);
  };

  const handleSave = () => {
    if (!hasUnsavedChanges) {
      toast.info('No changes to save');
      return;
    }
    updateMutation.mutate(htmlContent);
  };

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <p className="text-muted-foreground">Loading template...</p>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <p className="text-destructive">Error loading template</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold">{template.name}</h2>
            {template.description && (
              <p className="text-sm text-muted-foreground">{template.description}</p>
            )}
          </div>
        </div>
        {hasUnsavedChanges && (
          <div className="text-sm text-amber-600 dark:text-amber-500">
            Unsaved changes
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        <CertificateEditorLayout
          htmlContent={htmlContent}
          onChange={handleContentChange}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
        />
      </Card>
    </div>
  );
}
